import moment from "moment";
import { getI18n } from "react-i18next";
import { sendAssessmentEvents } from "../../../../network/network";
// PHKL Custom -s
import {
  PHKCandidateLanding,
  PHKCandidateLandingScreenName,
  PHKLCandidateGAFeature,
  PHKLCandidateGAModule,
  PHKLOBJECT_TYPE,
} from "../../../../ga/types/phkl-ga-parameter-type";
import { takePHKLGAUIClickEvent } from "../../../../ga/ga";
import { getTargetCandidate } from "../../../../utils";
// PHKL Custom -e

export const enum SmsTypeEnum {
  POP = "PRUdnaPOP",
  SCREEN = "PRUdnaScreen",
  EXPRESS = "PRUdnaExpress",
  CAREER_CHOICE = "CareerChoice", // LIMRA data type
}

interface AssessmentResultConfigType {
  message?: string;
  buttons?: { text: string; onPress: (cb?: () => void) => void }[];
  title?: string;
  iconName?: string;
  time?: string;
  leftConfig?: { label: string; value: string }[];
  rightConfig?: { label: string; value: string }[];
}

export const convertAssessments = ({
  assessment,
  isCandidateActive,
}: {
  assessment?: any;
  isCandidateActive: boolean;
}): AssessmentResultConfigType => {
  const isSmgData = assessment?.assessmentType !== SmsTypeEnum.CAREER_CHOICE;

  if (!assessment?.examLink) {
    return {
      message: getI18n().t("Recruitment.not_received_prudna_test_invite"),
    };
  }

  if (!assessment?.examResultLink) {
    if (
      !isSmgData &&
      (assessment?.resultSummary?.prospecting_courage ||
        assessment?.resultSummary?.entrepreneurial_spirit ||
        assessment?.resultSummary?.closing_confidence)
    ) {
      return getPRUdnaReportResultForCandidate(assessment);
    }

    if (assessment.assessmentStartedTime?.length) {
      return {
        title: isSmgData ? `PRUdna (${assessment.assessmentType.replace("PRUdna", "")})` : "PRUdna",
        message: getI18n().t(
          "Recruitment.you_have_not_completed_your_prudna_test_click_to_start_over_again",
        ),
        buttons: isCandidateActive
          ? [
              {
                text: getI18n().t("Recruitment.start_again"),
                onPress: () => {
                  // PHKL Custom -s
                  const candidate = getTargetCandidate();
                  takePHKLGAUIClickEvent({
                    module: PHKLCandidateGAModule,
                    feature: PHKLCandidateGAFeature,
                    journey: PHKCandidateLanding,
                    stage: "view_prudna_session",
                    screen_name: PHKCandidateLandingScreenName,
                    object_name: "start_again",
                    object_type: PHKLOBJECT_TYPE.BUTTON,
                    item_value: candidate?.candidateCode,
                    item_value_2: candidate?.agentid,
                    item_value_3: assessment?.assessmentType,
                    item_value_4: "assessment_started",
                    item_value_5: languageCodeMapping[assessment?.examLanCode] || "en",
                  });
                  // PHKL Custom -e
                  onPressCandidateReportedCardButton1(assessment);
                },
              },
            ]
          : undefined,

        time: moment.utc(assessment.audit.lastUpdatedAt).local().format("DD/MM/yyyy HH:mm"),
      };
    }
    return {
      title: isSmgData ? `PRUdna (${assessment.assessmentType.replace("PRUdna", "")})` : "PRUdna",
      message: getI18n().t("Recruitment.received_prudna_test_invite_start_here"),
      buttons: isCandidateActive
        ? [
            {
              text: getI18n().t("Recruitment.start_test"),
              onPress: () => {
                // PHKL Custom -s
                const candidate = getTargetCandidate();
                takePHKLGAUIClickEvent({
                  module: PHKLCandidateGAModule,
                  feature: PHKLCandidateGAFeature,
                  journey: PHKCandidateLanding,
                  stage: "view_prudna_session",
                  screen_name: PHKCandidateLandingScreenName,
                  object_name: "start_test",
                  object_type: PHKLOBJECT_TYPE.BUTTON,
                  item_value: candidate?.candidateCode,
                  item_value_2: candidate?.agentid,
                  item_value_3: assessment?.assessmentType,
                  item_value_4: "assessment_sent",
                  item_value_5: languageCodeMapping[assessment?.examLanCode] || "en",
                });
                // PHKL Custom -e
                onPressCandidateReportedCardButton1(assessment);
              },
            },
          ]
        : undefined,
      time: moment.utc(assessment.audit.lastUpdatedAt).local().format("DD/MM/yyyy HH:mm"),
    };
  } else {
    return !isSmgData
      ? getPRUdnaReportResultForCandidate(assessment)
      : {
          title: `PRUdna (${assessment.assessmentType.replace("PRUdna", "")})`,
          message: getI18n().t("Recruitment.you_have_finished_this_test"),
          iconName: "report",
          time: moment.utc(assessment.audit.lastUpdatedAt).local().format("DD/MM/yyyy HH:mm"),
          buttons: [
            {
              text: getI18n().t("Recruitment.view_report"),
              onPress: () => onPressCandidateReportedCardButton1(assessment, true),
            },
          ],
        };
  }
};

const onPressCandidateReportedCardButton1 = (assessment: any, viewReport?: boolean) => {
  let link = assessment.examLink;
  if (viewReport) {
    link = assessment.resultLinks?.candidate?.find((report: { link: string; lang: string }) => {
      return report.lang === (languageMapping[getI18n().language] || "eng");
    })?.link;
  } else {
    sendAssessmentEvents("CANDIDATE_ASSESSMENT_STARTED", assessment?.candidate_id, assessment?.id);
  }

  window.open(link, "_blank");
};

// for limra data display in candidate report card, convert the assessment result to show strengths and areas for growth
const getPRUdnaReportResultForCandidate = (assessment: any) => {
  const candidatePrudnaConfigs = [
    {
      label: getI18n().t("Recruitment.prospecting_courage"),
      value: assessment?.resultSummary?.prospecting_courage,
    },
    {
      label: getI18n().t("Recruitment.closing_confidence"),
      value: assessment?.resultSummary?.closing_confidence,
    },
    {
      label: getI18n().t("Recruitment.entrepreneurial_spirit"),
      value: assessment?.resultSummary?.entrepreneurial_spirit,
    },
  ];
  const leftConfig = candidatePrudnaConfigs.filter(
    (config) => config.value?.toLowerCase() !== "low",
  );
  const rightConfig = candidatePrudnaConfigs.filter(
    (config) => config.value?.toLowerCase() === "low",
  );
  const result = {
    title: "PRUdna",
    leftConfig,
    rightConfig,
    time: moment.utc(assessment.examCompleteAt).local().format("DD/MM/yyyy HH:mm"), //PHKL Custom
    buttons: assessment?.examResultLink
      ? [
          {
            text: getI18n().t("Recruitment.view_report"),
            onPress: () => {
              // PHKL Custom -s
              const candidate = getTargetCandidate();
              takePHKLGAUIClickEvent({
                module: PHKLCandidateGAModule,
                feature: PHKLCandidateGAFeature,
                journey: PHKCandidateLanding,
                stage: "view_prudna_session",
                screen_name: PHKCandidateLandingScreenName,
                object_name: "view_report",
                object_type: PHKLOBJECT_TYPE.BUTTON,
                item_value: candidate?.candidateCode,
                item_value_2: candidate?.agentid,
                item_value_3: assessment?.assessmentType,
                item_value_4: "assessment_submitted",
                item_value_5: languageCodeMapping[assessment?.examLanCode] || "en",
              });
              // PHKL Custom -e
              onPressCandidateReportedCardButton1(assessment, true);
            },
          },
        ]
      : undefined,
  };
  return result;
};

const languageMapping: { [key: string]: string } = {
  zhhk: "cht",
  zhCn: "chi",
  en: "eng",
  id: "ind",
  ms: "mal",
  vi: "vie",
};

// PHKL Custom -s
const languageCodeMapping: Record<string, string> = {
  cht: "zh-Hant",
  chi: "zh-hans",
  eng: "en",
};
// PHKL Custom -e
